import './TopHeader.scss';

import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ROUTE_ORDERS, ROUTE_QUOTES } from '@/common/constants';
import { useConfig } from '@/common/contexts/ConfigContext';
import { ReactComponent as OrderSvg } from '@/ebat/assets/icons/order.svg';
import { ReactComponent as QuoteSvg } from '@/ebat/assets/icons/quote.svg';
import CartButton from '@/ebat/components/blocks/Header/CartButton/CartButton';
import HeaderNavLink from '@/ebat/components/blocks/Header/HeaderNavLink/HeaderNavLink';
import LogoHomeLink from '@/ebat/components/blocks/Header/LogoHomeLink/LogoHomeLink';
import MobileMenuButton from '@/ebat/components/blocks/Header/MobileMenu/MobileMenuButton/MobileMenuButton';
import ProductsMenuButton from '@/ebat/components/blocks/Header/ProductsMenu/ProductsMenuButton/ProductsMenuButton';
import ScanButton from '@/ebat/components/blocks/Header/ScanButton/ScanButton';
import SearchBar from '@/ebat/components/blocks/Header/SearchBar/SearchBar';
import UserMenuButton from '@/ebat/components/blocks/Header/UserMenu/UserMenuButton/UserMenuButton';
import { useHeaderAccessesContext } from '@/ebat/contexts/Header/HeaderAccessesContext/HeaderAccessesContext';
import {
  MENU_NAME_SEARCH,
  MENU_TYPE_EXTERNAL,
  useHeaderMenuContext,
} from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';

import {
  TopHeaderDefaultProps,
  TopHeaderPropTypes,
} from './TopHeader.proptypes';

const TopHeader = ({
  className,
  mobileItemClassName,
  desktopItemClassName,
  showSearchBar,
}) => {
  const { t } = useTranslation();
  const { isEbat, isChrono } = useConfig();
  const { isOpen, handleClose } = useHeaderMenuContext();
  const {
    quotes: hasQuotesAccesses,
    orders: hasOrdersAccesses,
  } = useHeaderAccessesContext();
  const isSearchOpen = isOpen({
    name: MENU_NAME_SEARCH,
    type: MENU_TYPE_EXTERNAL,
  });

  return (
    <div
      role="presentation"
      className={cn(className, 'top-header')}
      onClick={handleClose}
    >
      <MobileMenuButton
        className={cn('top-header__mobile-menu-button', mobileItemClassName)}
      />
      <ProductsMenuButton
        className={cn('top-header__products-menu-button', desktopItemClassName)}
      />
      <LogoHomeLink className="top-header__logo" />
      <div
        className="top-header__search-container"
        data-qa-id="top-header-search-container"
        data-qa-value={cn({ opened: isSearchOpen }, { closed: !isSearchOpen })}
      >
        {showSearchBar && (
          <SearchBar
            className={cn('top-header__search-bar', {
              'top-header__search-bar--open': isSearchOpen,
              'top-header__search-bar--chrono-open': isChrono && isSearchOpen,
            })}
          />
        )}
      </div>
      {isEbat && (
        <HeaderNavLink
          className={cn('top-header__nav-link', desktopItemClassName)}
          context={cn(
            { 'menu-btn-quotes-active': hasQuotesAccesses },
            { 'menu-btn-quotes-disabled': !hasQuotesAccesses },
          )}
          Icon={<QuoteSvg />}
          label={t('ebat:entities:layout.header.my_account.quotes_span')}
          to={ROUTE_QUOTES}
          disabled={!hasQuotesAccesses}
        />
      )}
      <HeaderNavLink
        className={cn('top-header__nav-link', desktopItemClassName)}
        context={cn(
          { 'menu-btn-orders-active': hasOrdersAccesses },
          { 'menu-btn-orders-disabled': !hasOrdersAccesses },
        )}
        Icon={<OrderSvg />}
        label={t('ebat:entities:layout.header.my_account.orders_span')}
        to={ROUTE_ORDERS}
        disabled={!hasOrdersAccesses}
      />
      <UserMenuButton className={cn('top-header__user-menu-button')} />
      <ScanButton
        className={cn('top-header__scan-button', mobileItemClassName)}
      />
      <CartButton className={cn('top-header__cart-nav-link')} />
    </div>
  );
};

TopHeader.defaultProps = {
  ...TopHeaderDefaultProps,
};

TopHeader.propTypes = {
  ...TopHeaderPropTypes,
};

export default TopHeader;
